import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft, FaMoneyBill, FaTruck, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { getOrderDetails } from '../actions/orderActions';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';

const OrderDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  
  const orderDetails = useSelector(state => state.orderDetails);
  const { loading, error, order, items } = orderDetails;
  
  useEffect(() => {
    dispatch(getOrderDetails(id));
  }, [dispatch, id]);
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`;
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'shipped':
        return '已发货';
      case 'delivered':
        return '已送达';
      case 'cancelled':
        return '已取消';
      default:
        return status;
    }
  };
  
  return (
    <div className="order-detail-page">
      <div className="mb-4">
        <Link to="/orders" className="flex items-center text-gray-600 hover:text-red-700">
          <FaArrowLeft className="mr-1" />
          <span>返回订单列表</span>
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold mb-6">订单详情 #{id}</h1>
      
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="error">{error}</Message>
      ) : order && Object.keys(order).length === 0 ? (
        <Message variant="error">未找到订单</Message>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="border-b bg-gray-50 px-6 py-4">
                <h2 className="text-xl font-semibold">订单状态</h2>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600">状态:</span>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(
                      order.status
                    )}`}
                  >
                    {getStatusText(order.status)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600">订单日期:</span>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                
                {order.updated_at && order.updated_at !== order.created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">最后更新:</span>
                    <span>{formatDate(order.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="border-b bg-gray-50 px-6 py-4">
                <h2 className="text-xl font-semibold">订单商品</h2>
              </div>
              
              <div className="p-6">
                {items && items.length === 0 ? (
                  <Message>没有商品</Message>
                ) : (
                  <div className="divide-y">
                    {items && items.map((item) => (
                      <div key={item.id} className="flex py-4 first:pt-0 last:pb-0">
                        <div className="w-20 h-20 mr-4 flex-shrink-0">
                          <img
                            src={item.image_url || '/images/default-wine.jpg'}
                            alt={item.name}
                            className="w-full h-full object-cover rounded"
                          />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-medium">
                            <Link
                              to={`/product/${item.product_id}`}
                              className="hover:text-red-700"
                            >
                              {item.name}
                            </Link>
                          </h3>
                          
                          {item.chinese_name && (
                            <p className="text-sm text-gray-500">{item.chinese_name}</p>
                          )}
                          
                          <div className="mt-2 flex justify-between">
                            <span className="text-gray-600">
                              ¥{parseFloat(item.price).toFixed(2)} x {item.quantity}
                            </span>
                            <span className="font-medium">
                              ¥{(parseFloat(item.price) * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="border-b bg-gray-50 px-6 py-4">
                <h2 className="text-xl font-semibold">订单摘要</h2>
              </div>
              
              <div className="p-6">
                <div className="border-b pb-4 mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">小计</span>
                    <span>¥{parseFloat(order.total_amount).toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">配送费</span>
                    <span>¥0.00</span>
                  </div>
                </div>
                
                <div className="flex justify-between font-bold text-lg">
                  <span>总计</span>
                  <span>¥{parseFloat(order.total_amount).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="border-b bg-gray-50 px-6 py-4">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaMoneyBill className="mr-2 text-green-600" />
                  支付信息
                </h2>
              </div>
              
              <div className="p-6">
                <p className="text-gray-700">
                  支付方式: <span className="font-medium">货到付款</span>
                </p>
                <p className="text-gray-700 mt-1">
                  支付状态: <span className="font-medium text-yellow-600">待支付</span>
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b bg-gray-50 px-6 py-4">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaTruck className="mr-2 text-blue-600" />
                  配送信息
                </h2>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-red-600 mt-1 mr-2" />
                    <div>
                      <h3 className="font-medium mb-1">收货地址</h3>
                      <p className="text-gray-700">{order.shipping_address}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-start">
                    <FaPhone className="text-green-600 mt-1 mr-2" />
                    <div>
                      <h3 className="font-medium mb-1">联系电话</h3>
                      <p className="text-gray-700">{order.contact_phone}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;